import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import {
  type Bounds,
  NODE_LABEL_FONT,
  NODE_LABEL_FONT_SIZE,
  type NodeData,
  type NodeId,
  type NodeStyle,
  type NodeType,
  brand,
  getNodeDataBounds,
  makeTypeGuardByPropertyValue,
  nodeIconDefaultOptions,
  nodeTypeIcons,
} from '@ipfabric/utils';
import { plainMap } from '@ipfabric/utils/fp/plainMap';
import { prop } from '@ipfabric/utils/fp/prop';
import type { RectangleXywh } from 'components/Graph/GraphApp/helpers/RectangleXywh';
import {
  type GraphDisplayObject,
  createGraphDisplayObject,
} from 'components/Graph/GraphApp/helpers/createGraphDisplayObject';
import type {
  NodeLabelReasonToHide,
  NodeReasonToHide,
} from 'components/Graph/GraphApp/hideReasons';
import type { GraphRendererPluginInterface } from 'components/Graph/GraphApp/plugins/GraphRendererPlugin';
import { ZINDEX_NODE } from 'components/Graph/GraphApp/zIndexes';
import { getNodeIconSize } from 'components/Graph/helpers/getNodeIconSize';
import { getGraphNodeIconTexture } from 'components/Graph/resources/nodeIcons';
import { option } from 'fp-ts';
import type { Option } from 'fp-ts/lib/Option';
import { pipe } from 'fp-ts/lib/function';
import { BitmapText, type DisplayObject, Graphics, Sprite } from 'pixi.js';
import type PIXI from 'pixi.js';
import {
  type CompositeHiding,
  installCompositeHiding,
} from 'utils/pixi/compositeHiding';
import { ANCHOR_CENTER } from 'utils/pixi/constants';
import { getBitmapTextAnchor } from 'utils/pixi/getBitmapTextAnchor';
import { isTextureLoaded } from 'utils/pixi/isTextureLoaded';
import { waitForTextureLoad } from 'utils/pixi/waitForTextureLoad';
import { prefixer } from 'utils/prefixer';

export type GraphNodeDisplayObject = GraphDisplayObject<
  'node',
  NodeReasonToHide
>;
export const isGraphNodeDisplayObject = makeTypeGuardByPropertyValue<
  DisplayObject,
  GraphNodeDisplayObject
>()('graphDisplayObjectType', 'node');

export type GraphNodeLabelDisplayObject = BitmapText &
  CompositeHiding<NodeLabelReasonToHide>;

// Display object names
const ICON_SPRITE_NAME = 'icon';
const LABEL_BITMAP_TEXT_NAME = 'label';
const SELECTION_MARKER_NAME = 'selectionMarker';

// Style values
const SELECTION_MARKER_SIZE = 15;

export interface GraphNode {
  data: NodeData;
  displayObject: GraphNodeDisplayObject;
}

export const graphNodeFromNodeData =
  (displayObjects: Map<NodeId, GraphNodeDisplayObject>) =>
  (data: NodeData): Option<GraphNode> =>
    pipe(
      data,
      prop('id'),
      plainMap.getFrom(displayObjects),
      option.map((displayObject) => ({
        data,
        displayObject,
      })),
    );

export const setGraphNodeSpriteIcon = (
  iconSprite: PIXI.Sprite,
  nodeType: NodeType,
  requestRender: () => Promise<void>,
  nodeStyle: NodeStyle = nodeIconDefaultOptions.style,
): void => {
  iconSprite.texture = getGraphNodeIconTexture(nodeType, nodeStyle);
  if (!isTextureLoaded(iconSprite.texture)) {
    waitForTextureLoad(iconSprite.texture).then(requestRender);
  }
  const size = nodeTypeIcons[nodeType].getSize(nodeStyle);
  iconSprite.width = size.width;
  iconSprite.height = size.height;
};

const [idToName, nameToId] = prefixer('n/', (str) => {
  throw new Error(`Invalid node name ${str}`);
});

export function getNodeId(nodeDisplayObject: GraphNodeDisplayObject): NodeId {
  return brand<NodeId>(nameToId(nodeDisplayObject.name));
}

export function createGraphNodeDisplayObject(
  id: NodeId,
  nodeData: NodeData,
  pluginInterface: GraphRendererPluginInterface,
): GraphNodeDisplayObject {
  const node: GraphNodeDisplayObject = createGraphDisplayObject(
    pluginInterface,
    'node',
    () =>
      pluginInterface.isPeekingAtHiddenNodes()
        ? emitNodeUnhiddenBoundsChange(pluginInterface, id, node)
        : emitNodeHiddenBoundsChange(pluginInterface, id, node),
    ['USER_HIDDEN'],
  );
  node.name = idToName(id);
  node.zIndex = ZINDEX_NODE;

  const selectionMarker = new Graphics();
  selectionMarker.name = SELECTION_MARKER_NAME;
  node.addChild(selectionMarker);

  const iconSprite = new Sprite();
  iconSprite.name = ICON_SPRITE_NAME;
  iconSprite.anchor.set(ANCHOR_CENTER, ANCHOR_CENTER);
  node.addChild(iconSprite);

  const label = createLabel(nodeData);
  node.addChild(label);

  updateGraphNodeDisplayObject(node, nodeData, null, pluginInterface);
  updateGraphNodeSelection(
    node,
    pluginInterface.getGraphData().selectedNodeIds.has(id),
  );
  node.interactive = true;
  return node;
}

function createLabel(nodeData: NodeData): GraphNodeLabelDisplayObject {
  const label: GraphNodeLabelDisplayObject = installCompositeHiding(
    new BitmapText(nodeData.label, {
      font: { name: NODE_LABEL_FONT, size: NODE_LABEL_FONT_SIZE },
    }),
  );
  label.name = LABEL_BITMAP_TEXT_NAME;
  return label;
}

function updateLabelPositions(
  node: GraphNodeDisplayObject,
  nodeData: NodeData,
) {
  const labelStyle = nodeTypeIcons[nodeData.type].getLabelStyle(nodeData.style);
  const label = getLabelBitmapText(node);
  label.position.set(labelStyle.offset.x, labelStyle.offset.y);
  getBitmapTextAnchor(label).set(labelStyle.anchor.x, labelStyle.anchor.y);
}

export function updateGraphNodeDisplayObject(
  node: GraphNodeDisplayObject,
  nodeData: NodeData,
  oldNodeData: NodeData | null,
  pluginInterface: GraphRendererPluginInterface,
): void {
  const effectiveIconType = nodeData.type;
  const nodeTypeIcon = nodeTypeIcons[effectiveIconType];

  if (
    nodeData.type !== oldNodeData?.type ||
    nodeData.style !== oldNodeData?.style
  ) {
    drawSelectionMarker(node, nodeData);
    updateLabelPositions(node, nodeData);
  }

  const iconSprite = getIconSprite(node);
  setGraphNodeSpriteIcon(
    iconSprite,
    effectiveIconType,
    pluginInterface.requestRender,
    nodeData.style,
  );
  node.position.set(nodeData.position.x, nodeData.position.y);

  node.hitArea = nodeTypeIcon.getHitArea(nodeData.style);

  const label = getLabelBitmapText(node);
  label.text = nodeData.label;
}

export function updateGraphNodeSelection(
  node: GraphNodeDisplayObject,
  selected: boolean,
): void {
  getSelectionMarker(node).visible = selected;
}

function drawSelectionMarker(
  node: GraphNodeDisplayObject,
  nodeData: NodeData,
): void {
  const nodeSize = getNodeIconSize(nodeData);
  const selectionMarker = getSelectionMarker(node);
  const width = nodeSize.width + 2 * SELECTION_MARKER_SIZE;
  const height = nodeSize.height + 2 * SELECTION_MARKER_SIZE;
  selectionMarker.pivot.set(width / 2, height / 2);
  selectionMarker.clear();
  selectionMarker.beginFill(0x128aff, 0.13);
  selectionMarker.drawRoundedRect(0, 0, width, height, 10);
  selectionMarker.endFill();
}

function getIconSprite(node: GraphNodeDisplayObject): Sprite {
  return node.getChildByName(ICON_SPRITE_NAME) as Sprite;
}

function getLabelBitmapText(
  node: GraphNodeDisplayObject,
): GraphNodeLabelDisplayObject {
  return node.getChildByName(
    LABEL_BITMAP_TEXT_NAME,
  ) as GraphNodeLabelDisplayObject;
}

function getSelectionMarker(node: GraphNodeDisplayObject): Graphics {
  return node.getChildByName(SELECTION_MARKER_NAME) as Graphics;
}

export function getNodeIconRectangle(nodeData: NodeData): RectangleXywh {
  const nodeSize = getNodeIconSize(nodeData);
  return {
    x: nodeData.position.x - nodeSize.width / 2,
    y: nodeData.position.y - nodeSize.height / 2,
    width: nodeSize.width,
    height: nodeSize.height,
  };
}

export function getNodeBounds(
  nodeData: NodeData,
  node: GraphNodeDisplayObject,
  evenIfHidden?: true,
): Option<Bounds> {
  if (!evenIfHidden && node.isHiddenFromBounds()) {
    return option.none;
  }
  return option.some(getNodeDataBounds(nodeData));
}

const emitNodeHiddenBoundsChange = (
  pluginInterface: GraphRendererPluginInterface,
  nodeId: NodeId,
  node: GraphNodeDisplayObject,
) => {
  const nodeData = pluginInterface.getGraphData().nodes[nodeId];
  if (!nodeData) {
    stumble(`Trying to hide node ${nodeId}, but its data were not found`);
    return;
  }
  const oldBounds = getNodeBounds(nodeData, node, true);
  pluginInterface.events.emit('nodeBoundsChange', {
    node,
    bounds: option.none,
  });
  pluginInterface.events.emit('nodeChange', {
    id: nodeId,
    node,
    nodeData,
    oldNodeData: nodeData,
    oldBounds,
    bounds: option.none,
  });
};

export function hideNode(
  pluginInterface: GraphRendererPluginInterface,
  nodeId: NodeId,
  reason: NodeReasonToHide,
): void {
  const node = pluginInterface.nodeDisplayObjects.get(nodeId);
  if (!node) {
    stumble(
      `Trying to hide node ${nodeId}, but its display object was not found`,
    );
    return;
  }
  const wasHiddenFromBounds = node.isHiddenFromBounds();
  node.reasonsToHide.add(reason);
  if (!wasHiddenFromBounds && node.isHiddenFromBounds()) {
    emitNodeHiddenBoundsChange(pluginInterface, nodeId, node);
  }
}

const emitNodeUnhiddenBoundsChange = (
  pluginInterface: GraphRendererPluginInterface,
  nodeId: NodeId,
  node: GraphNodeDisplayObject,
) => {
  // Fixed noUncheckedIndexedAccess NIM-19408
  const nodeData = pluginInterface.getGraphData().nodes[nodeId]!;
  const bounds = getNodeBounds(nodeData, node, true);

  pluginInterface.events.emit('nodeBoundsChange', { node, bounds });
  pluginInterface.events.emit('nodeChange', {
    id: nodeId,
    node,
    nodeData,
    oldNodeData: nodeData,
    oldBounds: option.none,
    bounds,
  });
};

export function unhideNode(
  pluginInterface: GraphRendererPluginInterface,
  nodeId: NodeId,
  reason: NodeReasonToHide,
): void {
  const node = pluginInterface.nodeDisplayObjects.get(nodeId);
  if (node !== undefined) {
    node.reasonsToHide.delete(reason);
    if (reason !== 'CULLED' && !node.isHiddenFromBounds()) {
      emitNodeUnhiddenBoundsChange(pluginInterface, nodeId, node);
    }
  }
}

export function toggleHideNode(
  pluginInterface: GraphRendererPluginInterface,
  nodeId: NodeId,
  reason: NodeReasonToHide,
  hide: boolean,
): void {
  (hide ? hideNode : unhideNode)(pluginInterface, nodeId, reason);
}

export function setLabelVisible(
  node: GraphNodeDisplayObject,
  visible: boolean,
  reason: NodeLabelReasonToHide,
): void {
  getLabelBitmapText(node).reasonsToHide[visible ? 'delete' : 'add'](reason);
}
